<template>
  <div class="row jd-row">
    <div class="col-md-11" :class="isApplicantJd ? 'jd-class' : ''">
      <div class="d-flex pt-2">
        <button 
          class="scouted-btn mr-2" 
          :class="{ 'scouted-btn-expired': isScoutedExpired }"
          disabled 
          v-if="isScoutedJd"
        >
          {{ scoutedButtonText }}
        </button>
      </div>
      <p
          v-html="highlightedJobTitle"
          class="clickable job-title"
          @click="openJdInfo(jd)"
        ></p>
      <div
        class="scrollable job-qualification"
        @click="openJdInfo(jd)"
        v-html="highlightedQualification"
      ></div>
    </div>
    <div class="col-md-1 p-0 radio-column">
      <input
        type="radio"
        :name="radioName"
        class="form-check-input match-checkbox radio-input"
        :class="matchButtonClass"
        :checked="isApplicantJd"
        :disabled="isSubmitting"
        @click="handleRadioClick(jd, isApplicantJd, $event)"
      />
      <p class="radio-label">
        選択
      </p>
    </div>
  </div>
</template>

<script>
export default {
  name: 'JDRow',
  props: ['jd', 'jdClass', 'isSubmitting', 'profile', 'applicantJD','radioName'],
  computed: {
    isApplicantJd() {
      return this.jdClass === 'highlighted' || 
            (this.applicantJD && this.applicantJD._id === this.jd._id);
    },
    isScoutedJd() {
      const { scouted_jobs } = this.profile;
      return scouted_jobs.some((jobDetails) => jobDetails.job === this.jd._id);
    },
    scoutedJobStatus() {
      if (!this.profile || !this.profile.scouted_jobs) return null;
      const scoutedJob = this.profile.scouted_jobs.find(
        (jobDetails) => jobDetails.job === this.jd._id
      );
      return scoutedJob ? scoutedJob.status : null;
    },
    isScoutedExpired() {
      return this.scoutedJobStatus === 'expired';
    },
    scoutedButtonText() {
      if (this.isScoutedExpired) {
        return 'スカウト希望期間終了';
      }
      return 'スカウト希望中';
    },
    matchButtonClass() {
      return this.jdClass === 'highlighted' ? 'match' : '';
    },
    matchButtonText() {
      return this.jdClass === 'highlighted' ? '選択解除' : '選択';
    },
    highlightedJobTitle() {
      if (!this.jd.highlights || !this.jd.highlights.length) {
        return this.jd.job_title;
      }

      const highlight = this.jd.highlights.find((h) => h.path === 'job_title');
      if (!highlight || !highlight.texts) {
        return this.jd.job_title;
      }

      const hits = highlight.texts
        .filter((text) => text.type === 'hit')
        .map((text) => text.value)
        .sort((a, b) => b.length - a.length);

      return this.highlightHits(
        this.highlightFullWidthSpaces(this.jd.job_title, hits),
        hits
      );
    },
    highlightedQualification() {
      return this.processJobQualification();
    },
  },
  methods: {
    openJdInfo(jd) {
      this.$emit('openJdInfo', jd);
    },
    handleRadioClick(jd, currentState, event) {
      if (currentState) {
        event.target.checked = false;
        this.matchJd(jd, false);
      } else {
        this.matchJd(jd, true);
      }
    },
    matchJd(jd) {
      this.$emit('matchJd', jd, this.isApplicantJd);
    },
    processJobQualification() {
      if (!this.jd.highlights || !this.jd.highlights.length) {
        return this.jd.qualification;
      }

      const highlight = this.jd.highlights.find(
        (h) => h.path === 'qualification'
      );
      if (!highlight || !highlight.texts) {
        return this.jd.qualification;
      }

      // Get all hit values and sort them by length
      const hitTexts = highlight.texts
        .filter((text) => text.type === 'hit')
        .sort((a, b) => b.value.length - a.value.length);

      // Check if any hit contains HTML or if the job description contains HTML
      if (
        hitTexts.some((text) => text.value.includes('<')) ||
        this.jd.job_description.includes('<')
      ) {
        // Handle HTML content
        let fullContent = this.jd.qualification;

        hitTexts.forEach((text) => {
          const escapedValue = this.escapeRegExp(text.value);
          // Create regex that doesn't match inside HTML tags
          const regex = new RegExp(`(${escapedValue})(?![^<]*>)`, 'g');
          fullContent = fullContent.replace(
            regex,
            `<span class="highlight-hit">$1</span>`
          );
        });

        return fullContent;
      } else {
        // Handle plain text while preserving non-hit texts
        let result = '';
        highlight.texts.forEach((text) => {
          if (text.type === 'hit') {
            // Find the corresponding sorted hit text
            const sortedHit = hitTexts.find((hit) => hit.value === text.value);
            result += `<span class="highlight-hit">${sortedHit.value}</span>`;
          } else {
            result += text.value;
          }
        });

        return result;
      }
    },
    escapeRegExp(string) {
      return string.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
    },
    highlightFullWidthSpaces(text, hits) {
      // Create patterns for all possible hit combinations with full-width space
      const patterns = hits.flatMap((hit1, index) =>
        hits.slice(index + 1).map((hit2) => ({
          pattern: new RegExp(
            `(${this.escapeRegExp(hit1)}　${this.escapeRegExp(
              hit2
            )})|(${this.escapeRegExp(hit2)}　${this.escapeRegExp(hit1)})`,
            'g'
          ),
          replacement: (_, g1, g2) => {
            const match = g1 || g2;
            const [first, second] = match.split('　');
            return `${first}<span class="highlight-hit">　</span>${second}`;
          },
        }))
      );

      // Apply all patterns at once
      return patterns.reduce(
        (text, { pattern, replacement }) => text.replace(pattern, replacement),
        text
      );
    },

    highlightHits(text, hits) {
      const highlightedPositions = new Set();

      return hits.reduce((text, hit) => {
        const pattern = new RegExp(this.escapeRegExp(hit), 'g');
        return text.replace(pattern, (match, offset) => {
          const positions = Array.from(
            { length: match.length },
            (_, i) => offset + i
          );

          if (positions.every((pos) => !highlightedPositions.has(pos))) {
            positions.forEach((pos) => highlightedPositions.add(pos));
            return `<span class="highlight-hit">${match}</span>`;
          }
          return match;
        });
      }, text);
    },
    formatHighlightText(texts) {
      if (texts.some((text) => text.value.includes('<'))) {
        // Handle HTML content
        let fullContent = this.jd.qualification;
        texts.forEach((text) => {
          if (text.type === 'hit') {
            // Escape special regex characters in the text value
            const escapedValue = text.value.replace(
              /[.*+?^${}()|[\]\\]/g,
              '\\$&'
            );
            // Create regex that doesn't match inside HTML tags
            const regex = new RegExp(`(${escapedValue})(?![^<]*>)`, 'g');
            fullContent = fullContent.replace(
              regex,
              `<span class="highlight-hit">$1</span>`
            );
          }
        });
        return fullContent;
      } else {
        // Handle plain text (existing logic for job_title)
        return texts
          .map((text) => {
            if (text.type === 'hit') {
              return `<span class="highlight-hit">${text.value}</span>`;
            }
            return text.value;
          })
          .join('');
      }
    },
  },
};
</script>

<style scoped>
.radio-column {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 8px;
}
.radio-input {
  width: 20px;
  height: 20px;
  margin-left: 2px;
  margin-right: 2px;
}
.radio-label {
  margin-top: 65px;
  text-align: center;
}
.jd-row {
  border: 1px solid #787878;
  min-height: 140px;
  margin: auto;
}
.jd-class {
  background-color: #2f80ed33;
}
.job-title {
  font-weight: 700;
  font-size: 12px;
}
.job-qualification {
  font-weight: 400;
  font-size: 12px;
}
.scouted-btn {
  min-width: 168px;
  height: 25px;
  font-weight: bold;
  text-align: center;
  font-size: 10px;
  padding: 5px 10px;
  border-radius: 20px;
  white-space: nowrap;
  display: flex;
  align-items: center;
  justify-content: center;
  background-color: #14B8A6;
  color: #fff;
  border: 1px solid #14B8A6;
}

.scouted-btn-expired {
  background-color: #C4C4C4;
  color: #515C6B;
  border: 1px solid #C4C4C4;
}
.scrollable {
  max-height: 160px;
  overflow: hidden auto;
}
.highlighted {
  background-color: #f6ae2d33;
}

.clickable {
  cursor: pointer;
  max-width: 580px;
  overflow-wrap: break-word;
}

td {
  padding: 5px !important;
  text-align: left !important;
  border: 2px solid #c4c4c4 !important;
}

.matchButtonTd {
  text-align: center !important;
  vertical-align: middle;
  border: 2px solid white !important;
  background-color: #fff !important;
}

.match-button {
  background: #006c6b;
  padding: 10px 0px !important;
  width: 50px !important;
  border-radius: 0px !important;
  font-size: 12px;
  min-width: 0px !important;
}
.match-button:hover {
  background: #006c6b;
}

.match {
  background: #fff;
  color: #006c6b;
}

.btn-success:focus {
  background: #006c6b !important;
  border-color: #006c6b !important;
  box-shadow: none !important;
}

td {
  padding: 5px !important;
  text-align: left !important;
  border: 2px solid #c4c4c4 !important;
}
</style>
